import { useState } from "react"
import { usePage } from "@inertiajs/react"
import { getCsrfToken } from "@/lib/csrf"
import { toast } from "@/components/ui/sonner"
import { useSoftphoneBusy } from "@/hooks/use-softphone-busy"
import { useSoftphoneConnection } from "@/hooks/use-softphone-connection"
import type { SharedVoiceConfigProps } from "@/types/v2/voice"

export interface UseStartCall {
  // Fires the outbound-call POST. No-op while disabled or a
  // request is already in flight.
  startCall: () => void
  // True while the POST is in flight (the caller spins its
  // button on this).
  busy: boolean
  // True when the Call affordance should render disabled —
  // voice off, no phone, or the agent is already on a call.
  disabled: boolean
  // Humane reason for the disabled state, for a tooltip /
  // aria-label. Null when the button is enabled.
  disabledReason: string | null
  // True while the agent's softphone has a live call. Exposed
  // so callers that render a distinct "on a call" affordance
  // (e.g. the inbox header's green Call button) share this
  // hook's single `useSoftphoneBusy` subscription instead of
  // opening their own — keeps `onCall` and `disabledReason`
  // from drifting.
  onCall: boolean
}

interface UseStartCallOptions {
  // Contact to dial. Outbound voice is server-driven: the
  // backend resolves the caller number and conversation
  // from the contact id.
  contactId: number
  // Already-normalized E.164 phone, or null when missing.
  // PSTN needs a number to dial, so a null phone disables
  // the Call affordance.
  phone: string | null
}

// Shared outbound-call starter used everywhere a "Call"
// affordance lives (contacts table, contact drawer, deal
// contact card, mobile contact show). Encapsulates the
// `POST /v2/channels/voice/calls` flow and the identical
// gating every callsite must apply:
//
//   - voice disabled (the agent has no Voximplant
//     credential — `voiceConfig.enabled` is false/absent)
//   - no phone on the contact
//   - the softphone hasn't actually connected yet (gating on
//     the live SDK connection, not just `voiceConfig.enabled`,
//     so a failed/connecting softphone can't fire a doomed call
//     that strands a zombie "already on a call" — CON-167)
//   - the softphone is already on a call (single-slot)
//
// Outbound voice never touches the SDK from the click: the
// backend starts the Voximplant scenario, which rings the
// agent's own softphone first (surfaced by the app-global
// `Softphone`). So on success there's nothing to do beyond
// stop spinning.
export function useStartCall({
  contactId,
  phone,
}: UseStartCallOptions): UseStartCall {
  const { props } = usePage<SharedVoiceConfigProps>()
  const voiceEnabled = props.voiceConfig?.enabled ?? false
  const onCall = useSoftphoneBusy()
  const connection = useSoftphoneConnection()
  const [busy, setBusy] = useState(false)

  const disabledReason = !voiceEnabled
    ? "Voice calling isn't set up yet"
    : !phone
      ? "Add a phone number to call"
      : connection.status !== "connected"
        ? connection.error ?? "Connecting your phone line — one sec"
        : onCall
          ? "You're already on a call"
          : null

  const disabled = disabledReason !== null

  async function startCall() {
    if (disabled || busy) return
    setBusy(true)

    try {
      const response = await fetch("/v2/channels/voice/calls", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        body: JSON.stringify({
          v2_channels_voice_calls_create_form: {
            contact_id: contactId,
          },
        }),
        credentials: "same-origin",
      })

      if (!response.ok) {
        // The server returns 422 with humane error sentences
        // (e.g. "No phone number is set up to call from").
        // Surface the first one; fall back to a generic
        // message so the user never sees a raw status code.
        let message = "We couldn't start the call. Please try again."
        try {
          const body = (await response.json()) as {
            errors?: Record<string, string>
          }
          const first = body.errors && Object.values(body.errors)[0]
          if (first) message = first
        } catch {
          // Non-JSON body — keep the generic message.
        }
        toast.error(message)
        return
      }

      // Success: the agent's softphone rings as the
      // outbound/Connecting leg via ActionCable. Nothing else
      // to do here.
    } catch {
      toast.error("We couldn't start the call. Please try again.")
    } finally {
      setBusy(false)
    }
  }

  return { startCall, busy, disabled, disabledReason, onCall }
}
